import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, FileText, Download, Eye, X } from 'lucide-react';
import { claimService, policyService } from '../api/mockRepo';
import { downloadClaimPDF } from '../utils/claimPdf';
import type { InsuranceClaim, InsurancePolicy } from '../types';
import { z } from 'zod';
import toast from 'react-hot-toast';

const claimSchema = z.object({
  policy_id: z.string().min(1, 'Policy is required'),
  invoice_id: z.string().min(1, 'Invoice ID is required'),
  preauth_id: z.string().optional(),
  claim_date: z.string().min(1, 'Claim date is required'),
  treatment_date: z.string().min(1, 'Treatment date is required'),
  diagnosis_code: z.string().min(1, 'Diagnosis code is required'),
  treatment_code: z.string().min(1, 'Treatment code is required'),
  billed_amount: z.number().min(0, 'Billed amount must be positive'),
  approved_amount: z.number().min(0).optional(),
  status: z.enum(['submitted', 'under_review', 'approved', 'partially_approved', 'denied', 'paid']),
  notes: z.string().optional()
});

type ClaimFormData = z.infer<typeof claimSchema>;

export default function Claims() {
  const [claims, setClaims] = useState<InsuranceClaim[]>([]);
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClaim, setEditingClaim] = useState<InsuranceClaim | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ClaimFormData>({
    resolver: zodResolver(claimSchema),
    defaultValues: {
      status: 'submitted'
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [claimsData, policiesData] = await Promise.all([
        claimService.getAll(),
        policyService.getAll()
      ]);
      
      setClaims(claimsData);
      setPolicies(policiesData);
    } catch (error) {
      console.error('Failed to load claims data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ClaimFormData) => {
    try {
      if (editingClaim) {
        const updated = await claimService.update(editingClaim.id, data);
        setClaims(claims.map(c => c.id === editingClaim.id ? updated : c) as InsuranceClaim[]);
        toast.success('Claim updated successfully');
      } else {
        const created = await claimService.create(data as any);
        setClaims([...claims, created]);
        toast.success('Claim created successfully');
      }
      
      setShowForm(false);
      setEditingClaim(null);
      reset();
    } catch (error) {
      console.error('Failed to save claim:', error);
      toast.error('Failed to save claim');
    }
  };

  const handleEdit = (claim: InsuranceClaim) => {
    setEditingClaim(claim);
    reset({
      policy_id: claim.policy_id,
      invoice_id: claim.invoice_id,
      preauth_id: claim.preauth_id || '',
      claim_date: claim.claim_date,
      treatment_date: claim.treatment_date,
      diagnosis_code: claim.diagnosis_code,
      treatment_code: claim.treatment_code,
      billed_amount: claim.billed_amount,
      approved_amount: claim.approved_amount || 0,
      status: claim.status,
      notes: claim.notes || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClaim(null);
    reset();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'partially_approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      case 'paid': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDownloadClaim = async (claim: InsuranceClaim) => {
    const policy = policies.find(p => p.id === claim.policy_id);
    if (policy) {
      downloadClaimPDF(claim, policy);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Insurance Claims</h1>
          <p className="text-sm text-neutral-600">Submit and track insurance claims</p>
        </div>
        <button 
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Claim
        </button>
      </header>

      {showForm && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {editingClaim ? 'Edit Claim' : 'New Claim'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Policy *
                </label>
                <select
                  {...register('policy_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Policy</option>
                  {policies.map(policy => (
                    <option key={policy.id} value={policy.id}>
                      {policy.policy_number} - {policy.insurance_company}
                    </option>
                  ))}
                </select>
                {errors.policy_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.policy_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice ID *
                </label>
                <input
                  {...register('invoice_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.invoice_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.invoice_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pre-auth ID
                </label>
                <input
                  {...register('preauth_id')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Date *
                </label>
                <input
                  type="date"
                  {...register('claim_date')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.claim_date && (
                  <p className="text-sm text-red-600 mt-1">{errors.claim_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Treatment Date *
                </label>
                <input
                  type="date"
                  {...register('treatment_date')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.treatment_date && (
                  <p className="text-sm text-red-600 mt-1">{errors.treatment_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Diagnosis Code *
                </label>
                <input
                  {...register('diagnosis_code')}
                  placeholder="e.g., I25.9"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.diagnosis_code && (
                  <p className="text-sm text-red-600 mt-1">{errors.diagnosis_code.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Treatment Code *
                </label>
                <input
                  {...register('treatment_code')}
                  placeholder="e.g., CPT-99213"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.treatment_code && (
                  <p className="text-sm text-red-600 mt-1">{errors.treatment_code.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billed Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('billed_amount', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.billed_amount && (
                  <p className="text-sm text-red-600 mt-1">{errors.billed_amount.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Approved Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('approved_amount', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  {...register('status')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="submitted">Submitted</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="partially_approved">Partially Approved</option>
                  <option value="denied">Denied</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : editingClaim ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Treatment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {claims.map((claim) => (
                <tr key={claim.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-blue-500 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {claim.claim_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {claim.claim_date}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{claim.treatment_code}</div>
                    <div className="text-sm text-gray-500">{claim.diagnosis_code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      Billed: ${claim.billed_amount.toLocaleString()}
                    </div>
                    {claim.approved_amount && (
                      <div className="text-sm text-gray-500">
                        Approved: ${claim.approved_amount.toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(claim.status)}`}>
                      {claim.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      onClick={() => handleEdit(claim)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownloadClaim(claim)}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
